(() => {
  const initX40 = (root) => {
  if (root.dataset.x40Ready === "true") return;
  root.dataset.x40Ready = "true";

  const buttons = [...root.querySelectorAll("[data-feature]")];
  const product = root.querySelector('[data-video-key="default"]');
  const featureVideoElements = new Map(
    [...root.querySelectorAll(".x40-hero-product[data-video-key]")]
      .filter((video) => video.dataset.videoKey !== "default")
      .map((video) => [video.dataset.videoKey, video])
  );
  let activeProductVideo = product;
  const instanceId = `x40-${Math.random().toString(36).slice(2, 9)}`;
  const featurePanels = new Map();
  buttons.forEach((button, index) => {
    const panel = button.closest(".x40-feature-item")?.querySelector(".x40-feature-copy");
    button.setAttribute("aria-expanded", "false");
    button.querySelector(".x40-selector__icon")?.setAttribute("aria-hidden", "true");
    if (!panel) return;
    panel.id = `copy-${index}-${instanceId}`;
    button.setAttribute("aria-controls", panel.id);
    panel.setAttribute("aria-hidden", "true");
    featurePanels.set(button, panel);
  });
  const featureContent = {
    iphone: {
      label: "For iPhone",
      copy: "Aluminum unibody. Optimized for performance and battery. Aluminum alloy is remarkably light and has exceptional thermal conductivity."
    },
    watch: {
      label: "For Apple Watch",
      copy: "A dedicated charging surface keeps Apple Watch secure, visible, and ready throughout the day."
    },
    airpods: {
      label: "For AirPods",
      copy: "A stable wireless charging base gives AirPods a precise place to recharge without adding clutter."
    }
  };
  const featureKeys = [...featureVideoElements.keys()];
  const mobileQuery = window.matchMedia("(max-width: 900px)");
  const defaultVideo = new URL(product.dataset.src || product.currentSrc || product.src, document.baseURI).href;
  const observedVideos = [...root.querySelectorAll(".x40-scroll-video")];
  const allFeatureVideos = [product, ...featureVideoElements.values()];
  const allVideos = [...allFeatureVideos, ...observedVideos];
  const preloadQueue = [];
  const queuedVideos = new Set();
  let preloadBusy = false;

  const updateResponsivePosters = () => {
    observedVideos.forEach((video) => {
      const poster = mobileQuery.matches ? video.dataset.posterMb : video.dataset.posterPc;
      if (poster && video.poster !== new URL(poster, document.baseURI).href) video.poster = poster;
    });
  };
  updateResponsivePosters();

  const hydrateVideo = (video) => {
    if (video.dataset.hydrated === "true") return;
    video.preload = "auto";
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute("data-src");
    }
    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });
    video.dataset.hydrated = "true";
    video.load();
  };

  const videoHost = (video) => video.closest(".x40-feature-panel, .x40-media-card, .x40-full-bleed-section");
  const setBuffering = (video, buffering) => videoHost(video)?.classList.toggle("x40-is-buffering", buffering);

  const prepareAndPlay = (video) => {
    queuedVideos.delete(video);
    video.dataset.wantsPlayback = "true";
    hydrateVideo(video);
    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) setBuffering(video, true);
    const playback = video.play();
    if (playback) playback.catch(() => {});
  };

  const drainPreloadQueue = () => {
    if (preloadBusy) return;
    const video = preloadQueue.shift();
    if (!video) return;
    queuedVideos.delete(video);
    if (video.dataset.hydrated === "true") {
      drainPreloadQueue();
      return;
    }
    preloadBusy = true;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      preloadBusy = false;
      video.removeEventListener("canplay", finish);
      video.removeEventListener("error", finish);
      window.clearTimeout(timeout);
      drainPreloadQueue();
    };
    const timeout = window.setTimeout(finish, 6000);
    video.addEventListener("canplay", finish, { once: true });
    video.addEventListener("error", finish, { once: true });
    hydrateVideo(video);
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) finish();
  };

  const queuePreload = (video, priority = false) => {
    if (video.dataset.hydrated === "true" || queuedVideos.has(video)) return;
    queuedVideos.add(video);
    priority ? preloadQueue.unshift(video) : preloadQueue.push(video);
    drainPreloadQueue();
  };

  allVideos.forEach((video) => {
    video.addEventListener("waiting", () => {
      const canControlIndicator = !allFeatureVideos.includes(video) || video === activeProductVideo;
      if (canControlIndicator && video.dataset.wantsPlayback === "true") setBuffering(video, true);
    });
    ["canplay", "playing", "ended", "error"].forEach((eventName) => {
      video.addEventListener(eventName, () => {
        const canControlIndicator = !allFeatureVideos.includes(video) || video === activeProductVideo;
        if (canControlIndicator) setBuffering(video, false);
      });
    });
    video.addEventListener("playing", () => {
      video.dataset.started = "true";
      video.dataset.autoPaused = "false";
    });
    video.addEventListener("ended", () => {
      video.dataset.wantsPlayback = "false";
      video.dataset.finished = "true";
    });
  });

  const featurePanel = root.querySelector(".x40-feature-panel");
  const mobileDetail = root.querySelector(".x40-mobile-feature-detail");
  const mobileDetailCard = root.querySelector(".x40-mobile-detail-card");
  const mobileClose = root.querySelector(".x40-mobile-detail-close");
  const mobilePrevious = root.querySelector(".x40-mobile-detail-arrow--prev");
  const mobileNext = root.querySelector(".x40-mobile-detail-arrow--next");
  featurePanel.setAttribute("aria-label", "X40 Turbo charging features");
  mobileDetail.setAttribute("aria-hidden", "true");
  mobileClose.setAttribute("aria-label", "Close feature details");
  mobilePrevious.setAttribute("aria-label", "Previous feature");
  mobileNext.setAttribute("aria-label", "Next feature");
  featureVideoElements.forEach((video) => video.setAttribute("aria-hidden", "true"));
  let activeFeature = null;
  const revealFeaturePanel = () => {
    if (featurePanel.classList.contains("x40-is-visible")) return;
    featurePanel.classList.add("x40-is-visible");
    product.dataset.played = "true";
    prepareAndPlay(product);
  };

  if ("IntersectionObserver" in window) {
    const featureObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealFeaturePanel();
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -78% 0px", threshold: 0 });
    featureObserver.observe(featurePanel);
  } else {
    revealFeaturePanel();
  }

  const playFeatureVideo = (key) => {
    const nextVideo = featureVideoElements.get(key);
    if (!nextVideo) return;
    const shouldRestart = nextVideo.dataset.started === "true";
    if (activeProductVideo !== nextVideo) {
      activeProductVideo.pause();
      activeProductVideo.dataset.wantsPlayback = "false";
      activeProductVideo.classList.remove("x40-is-active", "x40-is-changing");
      activeProductVideo.setAttribute("aria-hidden", "true");
      activeProductVideo = nextVideo;
      activeProductVideo.classList.add("x40-is-active", "x40-is-changing");
      activeProductVideo.removeAttribute("aria-hidden");
    }
    hydrateVideo(activeProductVideo);
    activeProductVideo.dataset.finished = "false";
    if (shouldRestart) {
      if (nextVideo.readyState >= HTMLMediaElement.HAVE_METADATA) {
        nextVideo.currentTime = 0;
      } else {
        nextVideo.addEventListener("loadedmetadata", () => { nextVideo.currentTime = 0; }, { once: true });
      }
    }
    prepareAndPlay(activeProductVideo);
    window.setTimeout(() => nextVideo.classList.remove("x40-is-changing"), 180);
  };

  const selectFeature = (key) => {
    activeFeature = key;
    buttons.forEach((item) => {
      const active = item.dataset.feature === key;
      item.setAttribute("aria-expanded", String(active));
      const panel = featurePanels.get(item);
      if (!panel) return;
      panel.classList.toggle("x40-is-collapsed", !active || mobileQuery.matches);
      panel.setAttribute("aria-hidden", String(!active || mobileQuery.matches));
    });

    if (mobileQuery.matches) {
      const content = featureContent[key];
      const label = document.createElement("strong");
      label.textContent = `${content.label}.`;
      mobileDetailCard.replaceChildren(label, document.createTextNode(` ${content.copy}`));
      featurePanel.classList.add("x40-is-detail");
      mobileDetail.setAttribute("aria-hidden", "false");
    }
    playFeatureVideo(key);
  };

  const closeMobileDetail = () => {
    activeFeature = null;
    featurePanel.classList.remove("x40-is-detail");
    mobileDetail.setAttribute("aria-hidden", "true");
    buttons.forEach((item) => item.setAttribute("aria-expanded", "false"));
    activeProductVideo.pause();
    activeProductVideo.dataset.wantsPlayback = "false";
    activeProductVideo.classList.remove("x40-is-active", "x40-is-changing");
    activeProductVideo.setAttribute("aria-hidden", "true");
    activeProductVideo = product;
    activeProductVideo.classList.add("x40-is-active");
    activeProductVideo.removeAttribute("aria-hidden");
    hydrateVideo(activeProductVideo);
    activeProductVideo.dataset.finished = "false";
    if (activeProductVideo.readyState >= HTMLMediaElement.HAVE_METADATA) {
      activeProductVideo.currentTime = 0;
    } else {
      product.addEventListener("loadedmetadata", () => { product.currentTime = 0; }, { once: true });
    }
    prepareAndPlay(activeProductVideo);
  };

  const warmFeatureVideo = (key) => {
    const video = featureVideoElements.get(key);
    if (video) queuePreload(video, true);
  };

  let featureCacheQueued = false;
  const beginFeatureCacheAfterDefault = () => {
    const activeSource = product.currentSrc || product.src;
    if (featureCacheQueued || activeSource !== defaultVideo) return;
    featureCacheQueued = true;
    featureKeys.forEach((key) => {
      const video = featureVideoElements.get(key);
      if (video) queuePreload(video);
    });
  };
  product.addEventListener("canplay", beginFeatureCacheAfterDefault);
  if (product.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) beginFeatureCacheAfterDefault();

  buttons.forEach((button) => {
    button.addEventListener("click", () => selectFeature(button.dataset.feature));
    let hoverTimer;
    button.addEventListener("pointerenter", () => {
      hoverTimer = window.setTimeout(() => warmFeatureVideo(button.dataset.feature), 160);
    });
    button.addEventListener("pointerleave", () => window.clearTimeout(hoverTimer));
    button.addEventListener("focus", () => warmFeatureVideo(button.dataset.feature), { once: true });
    button.addEventListener("touchstart", () => warmFeatureVideo(button.dataset.feature), { passive: true, once: true });
  });

  const moveMobileFeature = (direction) => {
    const currentIndex = Math.max(0, featureKeys.indexOf(activeFeature));
    const nextIndex = (currentIndex + direction + featureKeys.length) % featureKeys.length;
    selectFeature(featureKeys[nextIndex]);
  };

  mobileClose.addEventListener("click", closeMobileDetail);
  mobilePrevious.addEventListener("click", () => moveMobileFeature(-1));
  mobileNext.addEventListener("click", () => moveMobileFeature(1));
  mobileQuery.addEventListener("change", () => {
    updateResponsivePosters();
    if (!mobileQuery.matches && featurePanel.classList.contains("x40-is-detail")) closeMobileDetail();
  });

  if ("IntersectionObserver" in window) {
    const preloadObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        queuePreload(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "1200px 0px", threshold: 0 });

    [product, ...observedVideos].forEach((video) => preloadObserver.observe(video));

    const videoObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.played === "true") return;
        entry.target.dataset.played = "true";
        prepareAndPlay(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -78% 0px", threshold: 0 });
    observedVideos.forEach((video) => videoObserver.observe(video));

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        const isFeatureVideo = allFeatureVideos.includes(video);
        if (isFeatureVideo && video !== activeProductVideo) {
          video.pause();
          video.dataset.autoPaused = "false";
          setBuffering(video, false);
          return;
        }
        if (!entry.isIntersecting) {
          if (!video.paused && !video.ended) {
            video.pause();
            video.dataset.autoPaused = "true";
          }
          setBuffering(video, false);
          return;
        }
        if (video.dataset.autoPaused === "true" && video.dataset.finished !== "true") prepareAndPlay(video);
      });
    }, { rootMargin: "80px 0px", threshold: 0.06 });
    allVideos.forEach((video) => visibilityObserver.observe(video));
  } else {
    allVideos.forEach(queuePreload);
  }
  };

  const bootX40 = (scope = document) => {
    if (scope.matches?.("[data-x40-root]")) initX40(scope);
    scope.querySelectorAll?.("[data-x40-root]").forEach(initX40);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => bootX40(), { once: true });
  } else {
    bootX40();
  }
  if (!window.__x40ShopifySectionListener) {
    window.__x40ShopifySectionListener = true;
    document.addEventListener("shopify:section:load", (event) => bootX40(event.target));
  }
})();
